/**
 * Ops registry for Admin → Dev services.
 * Keep in sync with documents/SERVICES_AND_COSTS.md
 */

export const DEV_SERVICES_META = {
  firebaseProject: 'happy-puppy-reminder',
  domain: 'healthy-pet.app',
  updatedAt: '2026-09-09',
  docPath: 'documents/SERVICES_AND_COSTS.md',
};

/** @typedef {'high'|'medium'|'low'|'fee'|'free'} CostPriority */

/**
 * First admin table: Priority | Service | Price | Notes | Link
 * Dates and details live in `notes`. Keep price short.
 *
 * @typedef {{
 *   id: string,
 *   priority: CostPriority,
 *   service: string,
 *   price: string,
 *   notes: string,
 *   billingUrl?: string,
 *   billingUrlLabel?: string,
 *   billingUrl2?: string,
 *   billingUrl2Label?: string,
 * }} CostPriorityRow
 */

/** @type {CostPriorityRow[]} */
export const COST_PRIORITIES = [
  {
    id: 'apple-developer',
    priority: 'high',
    service: 'Apple Developer',
    price: '$99/yr',
    billingUrl: 'https://developer.apple.com/account',
    billingUrlLabel: 'Membership',
    billingUrl2: 'https://developer.apple.com/help/account/membership/renewal/',
    billingUrl2Label: 'Renewal help',
    notes: 'Paid until 13.07.2027. Renew ~$99 near that date (auto-renew / Membership details).',
  },
  {
    id: 'gcp-firebase',
    priority: 'high',
    service: 'GCP / Firebase',
    price: 'Usage',
    billingUrl:
      'https://console.cloud.google.com/billing/01575D-3D7E2A-C8A262?project=happy-puppy-reminder',
    billingUrlLabel: 'Billing',
    notes:
      'Blaze pay-as-you-go (Functions, Firestore, Storage, backups). Monthly invoice. Project happy-puppy-reminder.',
  },
  {
    id: 'domain',
    priority: 'high',
    service: 'Namecheap',
    price: '~$23/yr',
    billingUrl: 'https://ap.www.namecheap.com/domains/list/',
    billingUrlLabel: 'Domains',
    notes:
      'healthy-pet.app paid until 10.11.2026; next renewal ~$23. Login may need verification email fix.',
  },
  {
    id: 'expo-eas',
    priority: 'medium',
    service: 'Expo EAS',
    price: 'Free→paid',
    billingUrl: 'https://expo.dev/accounts/romanova.marina/settings/billing',
    billingUrlLabel: 'Billing',
    notes: 'Account romanova.marina. Pay when build minutes exceed free tier.',
  },
  {
    id: 'resend',
    priority: 'medium',
    service: 'Resend',
    price: '~3k free/mo',
    billingUrl: 'https://resend.com/settings/billing',
    billingUrlLabel: 'Billing',
    billingUrl2: 'https://resend.com/overview',
    billingUrl2Label: 'Usage',
    notes: 'Outbound noreply@. Free until ~3k emails/mo, then Pro. Check usage often.',
  },
  {
    id: 'revenuecat',
    priority: 'medium',
    service: 'RevenueCat',
    price: 'Free→%',
    billingUrl: 'https://app.revenuecat.com/',
    billingUrlLabel: 'Dashboard',
    notes: 'Plus App Store / Play commission on IAP. Check RC plan vs revenue.',
  },
  {
    id: 'google-play',
    priority: 'medium',
    service: 'Google Play',
    price: 'One-time',
    billingUrl: 'https://play.google.com/console/',
    billingUrlLabel: 'Console',
    notes: 'Developer account already active. No yearly fee; ongoing = IAP commission only.',
  },
  {
    id: 'netlify',
    priority: 'low',
    service: 'Netlify',
    price: 'Free→paid',
    billingUrl: 'https://app.netlify.com/teams/marina-projects/billing/general',
    billingUrlLabel: 'Billing',
    notes: 'Team marina-projects. Watch bandwidth / build minutes.',
  },
  {
    id: 'cloudinary-improvmx',
    priority: 'low',
    service: 'Cloudinary, ImprovMX',
    price: 'Free→paid',
    billingUrl: 'https://console.cloudinary.com/',
    billingUrlLabel: 'Cloudinary',
    billingUrl2: 'https://app.improvmx.com/',
    billingUrl2Label: 'ImprovMX',
    notes: 'Web photos (Cloudinary) + inbound email (ImprovMX). Usually free at current scale.',
  },
  {
    id: 'paypal',
    priority: 'fee',
    service: 'PayPal',
    price: '% / donate',
    billingUrl: 'https://www.paypal.com/myaccount/summary',
    billingUrlLabel: 'Account',
    notes: 'Web donations only — fee per payment, not a SaaS subscription.',
  },
  {
    id: 'free-stack',
    priority: 'free',
    service: 'TG, Turnstile, Analytics…',
    price: '$0',
    notes: 'Telegram, Turnstile, Analytics, Crashlytics, GitHub — usually free now.',
  },
];

/**
 * @typedef {'active'|'planned'|'legacy'|'uncertain'|'off'} ServiceStatus
 * @typedef {{
 *   id: string,
 *   name: string,
 *   purpose: string,
 *   where: string,
 *   money: string,
 *   status: ServiceStatus,
 *   notes?: string,
 * }} DevServiceRow
 */

/** @type {{ id: string, titleKey: string, rows: DevServiceRow[] }[]} */
export const SERVICE_SECTIONS = [
  {
    id: 'infra',
    titleKey: 'sections.infra',
    rows: [
      {
        id: 'firebase',
        name: 'Google Cloud / Firebase',
        purpose: 'Auth, Firestore, Storage, Functions v2, Secret Manager, Scheduler',
        where: 'web + mobile + functions',
        money: 'Blaze pay-as-you-go',
        status: 'active',
        notes: 'Project happy-puppy-reminder. Billing: console.cloud.google.com/billing/01575D-3D7E2A-C8A262',
      },
      {
        id: 'firestore',
        name: '└ Firestore',
        purpose: 'Database',
        where: 'both clients',
        money: 'usage + weekly backups',
        status: 'active',
        notes: 'See RN documents/FIRESTORE_BACKUPS.md',
      },
      {
        id: 'storage',
        name: '└ Cloud Storage',
        purpose: 'Photos / files (esp. mobile)',
        where: 'both (RN primary upload)',
        money: 'usage',
        status: 'active',
        notes: 'Bucket happy-puppy-reminder.firebasestorage.app',
      },
      {
        id: 'functions',
        name: '└ Cloud Functions',
        purpose: 'Email, Telegram, RC webhook, cron',
        where: 'healthy-pet/functions',
        money: 'usage + egress',
        status: 'active',
        notes: 'Region us-central1',
      },
      {
        id: 'auth',
        name: '└ Authentication',
        purpose: 'Email / Google / Apple',
        where: 'both',
        money: 'usually Blaze usage',
        status: 'active',
      },
      {
        id: 'analytics',
        name: '└ Analytics',
        purpose: 'App events',
        where: 'mobile',
        money: 'free',
        status: 'active',
      },
      {
        id: 'crashlytics',
        name: '└ Crashlytics',
        purpose: 'Crashes',
        where: 'mobile',
        money: 'free',
        status: 'active',
      },
      {
        id: 'netlify',
        name: 'Netlify',
        purpose: 'Web hosting, domain, Universal Links',
        where: 'web',
        money: 'free → paid',
        status: 'active',
        notes: 'Prod healthy-pet.app; demo pet-health-tracker.netlify.app',
      },
      {
        id: 'dns',
        name: 'DNS (NS1 via Netlify)',
        purpose: 'Nameservers for domain',
        where: 'ops',
        money: 'usually via Netlify DNS',
        status: 'active',
        notes: 'dns1–4.p08.nsone.net',
      },
      {
        id: 'domain',
        name: 'Domain — Namecheap',
        purpose: 'healthy-pet.app',
        where: 'ops',
        money: '~$23 / year',
        status: 'active',
        notes: 'Paid until 10.11.2026; expected renewal ~$23',
      },
      {
        id: 'github',
        name: 'GitHub',
        purpose: 'Source repos',
        where: 'both',
        money: 'usually free',
        status: 'active',
        notes: 'marina-projects/pets-health-tracker, healthy-pet-react-native',
      },
    ],
  },
  {
    id: 'email',
    titleKey: 'sections.email',
    rows: [
      {
        id: 'resend',
        name: 'Resend',
        purpose: 'Outbound mail (welcome, reset, reminders, admin)',
        where: 'functions',
        money: 'free → Pro',
        status: 'active',
        notes: 'From noreply@healthy-pet.app; secret RESEND_API_KEY',
      },
      {
        id: 'improvmx',
        name: 'ImprovMX',
        purpose: 'Inbound MX → personal inbox (info@ + aliases)',
        where: 'ops + contacts UI',
        money: 'free → paid',
        status: 'active',
        notes: 'Do not confuse with Resend',
      },
      {
        id: 'telegram',
        name: 'Telegram Bot API (+ Telegraf)',
        purpose: 'Reminders, account linking',
        where: 'functions + both UIs',
        money: 'free',
        status: 'active',
        notes: 'Default bot healthy_pet_app_bot',
      },
      {
        id: 'sendgrid',
        name: 'SendGrid',
        purpose: 'Former email provider',
        where: '—',
        money: '—',
        status: 'legacy',
        notes: 'Replaced by Resend — cancel if still billed',
      },
    ],
  },
  {
    id: 'security',
    titleKey: 'sections.security',
    rows: [
      {
        id: 'turnstile',
        name: 'Cloudflare Turnstile',
        purpose: 'Bot protection on web',
        where: 'web',
        money: 'usually free',
        status: 'uncertain',
        notes: 'Script present; verifyTurnstile Netlify fn missing — check if live',
      },
    ],
  },
  {
    id: 'media',
    titleKey: 'sections.media',
    rows: [
      {
        id: 'cloudinary',
        name: 'Cloudinary',
        purpose: 'Pet photo uploads (web)',
        where: 'web',
        money: 'free → paid',
        status: 'active',
        notes: 'Cloud dzlylm2jy, preset user_pets. Mobile → Firebase Storage',
      },
    ],
  },
  {
    id: 'payments',
    titleKey: 'sections.payments',
    rows: [
      {
        id: 'revenuecat',
        name: 'RevenueCat',
        purpose: 'Premium subscriptions, entitlements, webhook',
        where: 'mobile + functions',
        money: 'free → paid',
        status: 'active',
        notes: 'See RN documents/MONETIZATION.md',
      },
      {
        id: 'apple',
        name: 'Apple Developer / ASC',
        purpose: 'iOS, IAP, Sign in with Apple',
        where: 'mobile (+ web links)',
        money: '~$99/year + IAP cut',
        status: 'active',
        notes: 'App id 6780182589, bundle app.healthypet.mobile',
      },
      {
        id: 'play',
        name: 'Google Play Console',
        purpose: 'Android, IAP',
        where: 'mobile',
        money: 'one-time (paid) + commission on IAP',
        status: 'active',
        notes: 'Developer account already active',
      },
      {
        id: 'paypal',
        name: 'PayPal',
        purpose: 'Donations (web)',
        where: 'web',
        money: '% per donation',
        status: 'active',
        notes: 'Not a SaaS fee; mobile has donate copy only',
      },
    ],
  },
  {
    id: 'build',
    titleKey: 'sections.build',
    rows: [
      {
        id: 'eas',
        name: 'Expo / EAS Build & Submit',
        purpose: 'Dev client, cloud builds, submit',
        where: 'mobile',
        money: 'free → paid builds',
        status: 'active',
        notes: 'EAS projectId in app.json / eas.json',
      },
      {
        id: 'npm',
        name: 'npm registry',
        purpose: 'Dependencies',
        where: 'both',
        money: 'free',
        status: 'active',
      },
    ],
  },
  {
    id: 'planned',
    titleKey: 'sections.planned',
    rows: [
      {
        id: 'push',
        name: 'FCM / APNs / expo-notifications',
        purpose: 'Push notifications',
        where: 'mobile',
        money: 'usually within Firebase/Apple',
        status: 'planned',
      },
      {
        id: 'sentry',
        name: 'Sentry (or similar)',
        purpose: 'Error tracking',
        where: '—',
        money: 'SaaS plan',
        status: 'planned',
        notes: 'Mentioned in roadmap',
      },
      {
        id: 'pitr',
        name: 'Firestore PITR',
        purpose: 'Point-in-time recovery',
        where: 'Firebase',
        money: 'extra GCP cost',
        status: 'off',
      },
    ],
  },
];

/**
 * Manual expense log — edit here to show in Admin UI.
 * @type {{ id: string, period: string, service: string, amount: string, currency: string, comment: string }[]}
 */
export const EXPENSE_JOURNAL = [
  {
    id: 'apple-developer-term',
    period: 'until 2027-07-13',
    service: 'Apple Developer',
    amount: '99',
    currency: 'USD',
    comment: 'Membership valid until 13.07.2027',
  },
  {
    id: 'namecheap-domain-term',
    period: 'until 2026-11-10',
    service: 'Namecheap domain',
    amount: '~23',
    currency: 'USD',
    comment: 'healthy-pet.app — next renewal ~10.11.2026',
  },
];

export const QUARTERLY_CHECKLIST = [
  'Firebase / GCP billing — no surprises on Functions & Storage',
  'Resend — volume vs free tier',
  'Cloudinary — storage / bandwidth',
  'Netlify — bandwidth / build minutes',
  'EAS — build minutes this month',
  'Domain — renewal date & registrar',
  'Apple Developer — renewal date',
  'RevenueCat — plan vs revenue',
  'Legacy: SendGrid / unused subscriptions — cancel',
];
